import Swup from 'swup'
import SwupHeadPlugin from '@swup/head-plugin'
import SwupPreloadPlugin from '@swup/preload-plugin'
import SwupFragmentPlugin, { type Rule } from '@swup/fragment-plugin'
// import SwupDebugPlugin from '@swup/debug-plugin'
import { ref } from 'vue'
import { importScrollTrigger } from './gsap'
import { useNavContext } from '@/modules/website/main-navigation/composables'

const st = importScrollTrigger()

const isVisiting = ref(false)

const DOWNLOADS_BASE = '/downloads/'
const DOWNLOADS_GROUP = `${DOWNLOADS_BASE}:group/`
const DOWNLOADS_GROUP_CATEGORY = `${DOWNLOADS_GROUP}:category/`
const DOWNLOADS_GROUP_CATEGORY_SUBCATEGORY = `${DOWNLOADS_GROUP_CATEGORY}:subcategory/`

const DOWNLOADS_NOT_SEARCH_REGEX = /^\/downloads\/(?!search(?:\/|$)).+\/?$/
const DOWNLOADS_SEARCH_REGEX = /^\/downloads\/search(?:\/)?(?:\?.*)?$/

const downloadsContentFragments = [
    {
        name: 'Downloads Content Fragments 1',
        from: [
            DOWNLOADS_BASE,
        ],
        to: [
            DOWNLOADS_BASE,
            DOWNLOADS_NOT_SEARCH_REGEX,
            DOWNLOADS_GROUP_CATEGORY,
            DOWNLOADS_GROUP_CATEGORY_SUBCATEGORY
        ],
        containers: ['#downloads-content-fragments']
    },
    {
        name: 'Downloads Content Fragments 2',
        from: [
            DOWNLOADS_BASE,
            DOWNLOADS_NOT_SEARCH_REGEX,
            DOWNLOADS_GROUP_CATEGORY,
            DOWNLOADS_GROUP_CATEGORY_SUBCATEGORY
        ],
        to: [
            DOWNLOADS_BASE,
        ],
        containers: ['#downloads-content-fragments']
    },
] as Rule[]

const downloadsFilesListWithFilterFragments = [
    {
        name: 'Downloads Files List With Filter Fragments',
        from: [
            DOWNLOADS_GROUP_CATEGORY,
            DOWNLOADS_GROUP_CATEGORY_SUBCATEGORY
        ],
        to: [
            DOWNLOADS_GROUP_CATEGORY,
            DOWNLOADS_GROUP_CATEGORY_SUBCATEGORY
        ],
        containers: ['#downloads-files-list-with-filter-fragments']
    }
] as Rule[]

const downloadsSearchFragments = [
    {
        name: 'Downloads Search Fragments 1',
        from: [
            DOWNLOADS_BASE, 
            DOWNLOADS_SEARCH_REGEX,
            DOWNLOADS_NOT_SEARCH_REGEX,
            DOWNLOADS_GROUP_CATEGORY,
            DOWNLOADS_GROUP_CATEGORY_SUBCATEGORY,
        ],
        to: DOWNLOADS_SEARCH_REGEX,
        containers: ['#downloads-search-fragments']
    },
    {
        name: 'Downloads Search Fragments 2',
        from: [
            DOWNLOADS_SEARCH_REGEX,
        ],
        to: [
            DOWNLOADS_BASE, 
            DOWNLOADS_SEARCH_REGEX,
            DOWNLOADS_NOT_SEARCH_REGEX,
            DOWNLOADS_GROUP_CATEGORY,
            DOWNLOADS_GROUP_CATEGORY_SUBCATEGORY,
        ],
        containers: ['#downloads-search-fragments']
    },
] as Rule[]

const FAQ_BASE = '/faq/'
const FAQ_GROUP = `${FAQ_BASE}:group/`
const FAQ_GROUP_CATEGORY = `${FAQ_GROUP}:category/`
const FAQ_GROUP_CATEGORY_SUBCATEGORY = `${FAQ_GROUP_CATEGORY}:subcategory/`

const FAQ_NOT_SEARCH_REGEX = /^\/faq\/(?!search(?:\/|$))[^?]+(?:\?.*)?$/
const FAQ_SEARCH_REGEX = /^\/faq\/search(?:\/)?(?:\?.*)?$/

const faqContentFragments = [
    {
        name: 'FAQ Content Fragments 1',
        from: [
            FAQ_BASE,
        ],
        to: [
            FAQ_BASE,
            FAQ_NOT_SEARCH_REGEX,
            FAQ_GROUP_CATEGORY,
            FAQ_GROUP_CATEGORY_SUBCATEGORY
        ],
        containers: ['#faq-content-fragments']
    },
    {
        name: 'FAQ Content Fragments 2',
        from: [
            FAQ_BASE,
            FAQ_NOT_SEARCH_REGEX,
            FAQ_GROUP_CATEGORY,
            FAQ_GROUP_CATEGORY_SUBCATEGORY
        ],
        to: [
            FAQ_BASE,
        ],
        containers: ['#faq-content-fragments']
    },
] as Rule[]

const faqContentWithFilterFragments = [
    {
        name: 'FAQ Content With Filter Fragments',
        from: [
            FAQ_GROUP_CATEGORY,
            FAQ_GROUP_CATEGORY_SUBCATEGORY
        ],
        to: [
            FAQ_GROUP_CATEGORY,
            FAQ_GROUP_CATEGORY_SUBCATEGORY
        ],
        containers: ['#faq-content-with-filter-fragments']
    }
] as Rule[]

const faqSearchFragments = [
    {
        name: 'FAQ Search Fragments 1',
        from: [
            FAQ_BASE, 
            FAQ_SEARCH_REGEX,
            FAQ_NOT_SEARCH_REGEX,
            FAQ_GROUP_CATEGORY,
            FAQ_GROUP_CATEGORY_SUBCATEGORY,
        ],
        to: [FAQ_SEARCH_REGEX],
        containers: ['#faq-search-fragments']
    },
    {
        name: 'FAQ Search Fragments 2',
        from: [
            FAQ_SEARCH_REGEX,
        ],
        to: [
            FAQ_BASE, 
            FAQ_SEARCH_REGEX,
            // FAQ_NOT_SEARCH_REGEX,
            FAQ_GROUP_CATEGORY,
            FAQ_GROUP_CATEGORY_SUBCATEGORY,
        ],
        containers: ['#faq-search-fragments']
    },
] as Rule[]

const faqSearchToDetailFragments = [
    {
        name: 'FAQ Search To Detail Fragments 1',
        from: [
            FAQ_SEARCH_REGEX,
            FAQ_NOT_SEARCH_REGEX
        ],
        to: [
            FAQ_SEARCH_REGEX,
            FAQ_NOT_SEARCH_REGEX
        ],
        containers: ['#faq-search-to-detail-fragments']
    },
]

export const swup = new Swup({
    containers: ['#swup'],
    animationSelector: '[class*="page-transition-"]',
    plugins: [
        // new SwupDebugPlugin(),
        new SwupHeadPlugin({
            persistAssets: true,
        }),
        new SwupPreloadPlugin({
            preloadVisibleLinks: true
        }),
        new SwupFragmentPlugin({
            rules: [
                ...downloadsContentFragments,
                ...downloadsFilesListWithFilterFragments,
                ...downloadsSearchFragments,
                ...faqContentFragments,
                ...faqContentWithFilterFragments,
                ...faqSearchFragments,
                ...faqSearchToDetailFragments
            ],
            debug: true
        })
    ],
})

const { closeDesktopNav, closeMobileNavDialog } = useNavContext()

const trackPageview = () => {
    if (typeof window.dataLayer !== 'object') {
        console.warn('GTM is not loaded on the page')
        return
    }

    const url = window.location.pathname + window.location.search
    const title = document.title

    window.dataLayer.push({
        event: 'VirtualPageview',
        virtualPageURL: url,
        virtualPageTitle: title
    })

    swup.log(`GTM page view: ${url}`)

}

swup.hooks.on('page:view', () => {
    trackPageview()
})

swup.hooks.on('visit:start', (e) => {
    closeDesktopNav()
    closeMobileNavDialog()
    isVisiting.value = true
})

swup.hooks.on('visit:end', (e) => {
    isVisiting.value = false
    st.refresh()

    const anchor = e.to.hash

    if (anchor) {
        const anchorEl = document.querySelector(anchor)
        
        if (anchorEl) {
            anchorEl.scrollIntoView({ block: 'start', behavior: 'smooth' })
        }
    }
})

swup.hooks.on('animation:out:end', (e) => {
    const anchor = e.to.hash

    if (!anchor && e.scroll.reset) {
        window.scrollTo({ top: 0, behavior: 'instant' })
    }
})

export const useSwup = () => {
    return {
        isVisiting,
        swupVisit: (url: string) => swup.navigate(url)
    }
}
